// <s-product-gallery> drives the main media track with prev/next commands and keeps the
// thumbnail strip in sync. Native scroll-snap owns swiping; this only adds controls.
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll('[data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.goTo(this.#index - 1);
      else if (event.command === '--next') this.goTo(this.#index + 1);
      else if (event.command === '--go') this.goTo(Number(event.source?.dataset.index ?? 0));
    }, opts);

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncFromScroll());
    }, { ...opts, passive: true });

    this.#track.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.#index + (rtl ? -1 : 1)); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.#index + (rtl ? 1 : -1)); }
    }, opts);

    this.#update(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  goTo(target) {
    const count = this.#slides.length;
    if (!count) return;
    const index = (target + count) % count;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const slide = this.#slides[index];
    const trackRect = this.#track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    // Delta-based scrolling works in both LTR and RTL without reading scrollLeft sign conventions.
    this.#track.scrollBy({ left: slideRect.left - trackRect.left, behavior });
    this.#update(index);
  }

  #syncFromScroll() {
    const trackLeft = this.#track.getBoundingClientRect().left;
    let closest = 0;
    let best = Infinity;
    this.#slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < best) { best = distance; closest = i; }
    });
    if (closest !== this.#index) this.#update(closest);
  }

  #update(index) {
    this.#index = index;
    this.#thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) this.#revealThumb(thumb);
    });
  }

  #revealThumb(thumb) {
    const strip = thumb.parentElement?.closest('[data-gallery-thumbs]');
    if (!strip) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    // Scroll only the strip so the page itself never jumps.
    if (thumbRect.left < stripRect.left) strip.scrollBy({ left: thumbRect.left - stripRect.left - 8 });
    else if (thumbRect.right > stripRect.right) strip.scrollBy({ left: thumbRect.right - stripRect.right + 8 });
    // Vertical strips (side thumbnails) scroll on the block axis.
    if (thumbRect.top < stripRect.top) strip.scrollBy({ top: thumbRect.top - stripRect.top - 8 });
    else if (thumbRect.bottom > stripRect.bottom) strip.scrollBy({ top: thumbRect.bottom - stripRect.bottom + 8 });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
